// Employee certifications (Pat, Oct 5 2026) — the pieces shared by the phone's "My Cards",
// the desktop employee card and the Certifications page. Pictures never come from a public
// link: each is fetched with the session token from GET /certs/files/:id (the server only
// hands it to the employee or Office/Executive) and shown from a blob.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { API_BASE, getToken, apiFetch } from '../services/apiClient';
import PdfWindow from './PdfWindow';

export const fmtDay = ymd => { if (!ymd) return ''; const d = new Date(ymd + 'T12:00:00'); return isNaN(d) ? ymd : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };
const STATE = {
  expired: { bg: '#fee2e2', fg: '#b91c1c', bd: '#fecaca', text: c => `Expired ${fmtDay(c.expires_on)}` },
  red: { bg: '#fee2e2', fg: '#b91c1c', bd: '#fecaca', text: c => `Expires ${fmtDay(c.expires_on)} · ${c.days_left} day${c.days_left === 1 ? '' : 's'}` },
  yellow: { bg: '#fef9c3', fg: '#854d0e', bd: '#fde68a', text: c => `Expires ${fmtDay(c.expires_on)} · ${c.days_left} days` },
  ok: { bg: '#dcfce7', fg: '#166534', bd: '#bbf7d0', text: c => `Good through ${fmtDay(c.expires_on)}` },
  none: { bg: '#f1f5f9', fg: '#334155', bd: '#e2e8f0', text: () => 'No expiration' },
};
const REVIEW = {
  pending: { bg: '#eff6ff', fg: '#1d4ed8', bd: '#bfdbfe', text: 'Waiting for review' },
  rejected: { bg: '#fff7ed', fg: '#c2410c', bd: '#fed7aa', text: 'Sent back' },
};
const pill = s => ({ display: 'inline-block', fontSize: 11.5, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: s.bg, color: s.fg, border: `1px solid ${s.bd}`, whiteSpace: 'nowrap' });
const lbl = { fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 4 };
const inp = { border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 10px', fontSize: 14, width: '100%', boxSizing: 'border-box', background: '#fff', fontFamily: 'inherit' };
const btn = (bg = '#fff', fg = '#374151', border = '1px solid #e5e7eb') => ({ padding: '8px 14px', borderRadius: 8, border, background: bg, color: fg, fontSize: 13, fontWeight: 700, cursor: 'pointer' });

export function ExpiryBadge({ c }) { const s = STATE[c.expiry_state] || STATE.none; return <span style={pill(s)}>{s.text(c)}</span>; }
export function ReviewBadge({ status }) { const s = REVIEW[status]; return s ? <span style={pill(s)}>{s.text}</span> : null; }

// One picture, fetched with the token into a blob.
function useCertBlob(fileId) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let made = null, alive = true;
    fetch(`${API_BASE}/certs/files/${fileId}`, { headers: { 'x-worker-token': getToken() || '' } })
      .then(r => { if (!r.ok) throw new Error(r.status); return r.blob(); })
      .then(b => { if (!alive) return; made = URL.createObjectURL(b); setSrc(made); })
      .catch(() => alive && setFailed(true));
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [fileId]);
  return { src, failed };
}

function CertThumb({ file, title }) {
  const isPdf = /pdf/i.test(file.mime_type || '') || /\.pdf$/i.test(file.original_name || '');
  const { src, failed } = useCertBlob(isPdf ? null : file.id);
  const [open, setOpen] = useState(false);
  const [broken, setBroken] = useState(false);
  const box = { width: 92, height: 62, borderRadius: 8, border: '1px solid #e5e7eb', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'pointer', position: 'relative', flex: '0 0 auto' };
  const side = file.side ? <span style={{ position: 'absolute', left: 4, bottom: 3, fontSize: 9.5, fontWeight: 800, color: '#fff', background: 'rgba(15,23,42,0.65)', borderRadius: 4, padding: '0 4px', textTransform: 'uppercase' }}>{file.side}</span> : null;
  return (
    <>
      <div onClick={() => setOpen(true)} style={box} title={file.original_name || 'Open'}>
        {isPdf ? <span style={{ fontSize: 12, fontWeight: 800, color: '#b91c1c' }}>📄 PDF</span>
          : src && !broken ? <img src={src} alt={file.side || 'card'} onError={() => setBroken(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span style={{ fontSize: 11, color: '#94a3b8' }}>{failed ? 'Can’t show' : broken ? '📷 Open' : '…'}</span>}
        {side}
      </div>
      {open && (isPdf
        ? <PdfWindow fetchPath={`/certs/files/${file.id}`} title={title} fileName={file.original_name} onClose={() => setOpen(false)} />
        : <ImageWindow src={src} title={`${title}${file.side ? ' — ' + file.side : ''}`} onClose={() => setOpen(false)} />)}
    </>
  );
}

function ImageWindow({ src, title, onClose }) {
  return createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 6500, background: 'rgba(15,23,42,0.85)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ color: '#fff', fontWeight: 800, fontSize: 15, marginBottom: 10, display: 'flex', gap: 14, alignItems: 'center' }}>{title}<button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 12px', fontWeight: 700, cursor: 'pointer' }}>Close</button></div>
      {src ? <img src={src} alt={title} onClick={e => e.stopPropagation()} style={{ maxWidth: '100%', maxHeight: '82vh', borderRadius: 8, background: '#fff' }} />
        : <div style={{ color: '#cbd5e1' }}>This picture can’t be shown here (an iPhone HEIC file?). Ask for a JPG or a screenshot of it.</div>}
    </div>, document.body);
}

// Add a card (or, Office/Executive, add one for someone). forUserId null = me.
export function CertUploadForm({ types, forUserId = null, presetType = '', onSaved, onCancel }) {
  const [f, setF] = useState({ cert_type: presetType || '', other_name: '', issued_on: '', expires_on: '' });
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const t = (types || []).find(x => x.key === f.cert_type);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  async function save() {
    setErr('');
    if (!f.cert_type) return setErr('Pick what kind of card this is.');
    if (f.cert_type === 'other' && !f.other_name.trim()) return setErr('Say what the card is.');
    if (t && t.expires && !f.expires_on) return setErr('Put in the expiration date from the card.');
    if (!front) return setErr('Add a photo of the card.');
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => { if (v) fd.append(k, v); });
      fd.append('front', front); if (back) fd.append('back', back);
      const r = await fetch(`${API_BASE}${forUserId ? `/users/${forUserId}/certs` : '/me/certs'}`, { method: 'POST', headers: { 'x-worker-token': getToken() || '' }, body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `Could not save (${r.status})`);
      onSaved && onSaved(d);
    } catch (e) { setErr(e.message || 'Could not save the card.'); }
    setBusy(false);
  }
  const fileBox = (label, file, setFile, hint) => (
    <label style={{ flex: '1 1 140px', border: `1.5px dashed ${file ? '#16a34a' : '#cbd5e1'}`, borderRadius: 10, padding: '14px 10px', textAlign: 'center', cursor: 'pointer', background: file ? '#f0fdf4' : '#f8fafc' }}>
      <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
      <div style={{ fontSize: 22 }}>{file ? '✅' : '📷'}</div>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{label}</div>
      <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2, wordBreak: 'break-all' }}>{file ? file.name : hint}</div>
    </label>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <label style={lbl}>What card is it?</label>
        <select value={f.cert_type} onChange={e => set('cert_type', e.target.value)} style={inp}>
          <option value="">— pick one —</option>
          {(types || []).map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select>
      </div>
      {f.cert_type === 'other' && <div><label style={lbl}>Name of the card</label><input value={f.other_name} onChange={e => set('other_name', e.target.value)} placeholder="e.g. Forklift operator, IMSA Signal Tech" style={inp} /></div>}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {fileBox('Front', front, setFront, 'Take a photo or pick one')}
        {(!t || t.back) && fileBox('Back (if it has one)', back, setBack, 'Optional')}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 140px' }}><label style={lbl}>Issued (optional)</label><input type="date" value={f.issued_on} onChange={e => set('issued_on', e.target.value)} style={inp} /></div>
        <div style={{ flex: '1 1 140px' }}><label style={lbl}>Expires{t && !t.expires ? ' (if it does)' : ''}</label><input type="date" value={f.expires_on} onChange={e => set('expires_on', e.target.value)} style={inp} /></div>
      </div>
      <div style={{ fontSize: 11.5, color: '#6b7280' }}>🔒 Only you and the office can see these pictures. Don’t type your license number anywhere — the picture is enough.</div>
      {err && <div style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {onCancel && <button type="button" onClick={onCancel} disabled={busy} style={btn()}>Cancel</button>}
        <button type="button" onClick={save} disabled={busy} style={btn('#e87722', '#fff', 'none')}>{busy ? 'Uploading…' : forUserId ? 'Add the card' : 'Send to the office'}</button>
      </div>
    </div>
  );
}

// One card in a list: pictures, type, dates, its state — and for Office/Executive the
// review buttons. `mode`: 'own' (the employee) | 'manage' (Office/Executive).
export function CertRow({ c, mode, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [views, setViews] = useState(null);
  async function patch(body) {
    setBusy(true); setErr('');
    try { await apiFetch(`/certs/${c.id}`, { method: 'PATCH', body: JSON.stringify(body) }); setRejecting(false); onChanged && onChanged(); }
    catch (e) { setErr(e?.error || e?.message || 'Could not save.'); }
    setBusy(false);
  }
  async function remove() {
    if (!window.confirm(`Remove this ${c.label} card and its pictures?`)) return;
    setBusy(true);
    try { await apiFetch(`/certs/${c.id}`, { method: 'DELETE' }); onChanged && onChanged(); }
    catch (e) { setErr(e?.error || 'Could not remove it.'); }
    setBusy(false);
  }
  async function addPicture(file) {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('files', file);
      const r = await fetch(`${API_BASE}/certs/${c.id}/files`, { method: 'POST', headers: { 'x-worker-token': getToken() || '' }, body: fd });
      const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || 'Upload failed');
      onChanged && onChanged();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }
  const canEdit = mode === 'manage' || c.status !== 'approved';
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, background: c.status === 'approved' ? '#fff' : '#fcfcfd' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14.5, fontWeight: 800, color: '#111827', marginRight: 'auto' }}>{c.label}</span>
        <ReviewBadge status={c.status} />
        <ExpiryBadge c={c} />
      </div>
      {c.status === 'rejected' && c.review_note && <div style={{ fontSize: 12.5, color: '#c2410c' }}>“{c.review_note}”{c.reviewed_by_name ? ` — ${c.reviewed_by_name}` : ''}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {(c.files || []).map(fl => <CertThumb key={fl.id} file={fl} title={c.label} />)}
        {canEdit && <label style={{ ...btn(), padding: '6px 10px', fontSize: 12 }}>+ Picture<input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => addPicture(e.target.files && e.target.files[0])} /></label>}
      </div>
      <div style={{ fontSize: 11.5, color: '#6b7280' }}>
        {c.issued_on ? `Issued ${fmtDay(c.issued_on)} · ` : ''}Uploaded {fmtDay(String(c.created_at || '').slice(0, 10))}{c.uploaded_by_name && mode === 'manage' ? ` by ${c.uploaded_by_name}` : ''}
        {c.status === 'approved' && c.reviewed_by_name ? ` · approved by ${c.reviewed_by_name}` : ''}
      </div>
      {mode === 'manage' && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {c.status !== 'approved' && <button type="button" disabled={busy} onClick={() => patch({ status: 'approved' })} style={btn('#16a34a', '#fff', 'none')}>✓ Approve</button>}
          {c.status !== 'rejected' && <button type="button" disabled={busy} onClick={() => setRejecting(r => !r)} style={btn('#fff7ed', '#c2410c', '1px solid #fed7aa')}>Send back</button>}
          <label style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>Expires <input type="date" defaultValue={c.expires_on || ''} onBlur={e => { if ((e.target.value || null) !== (c.expires_on || null)) patch({ expires_on: e.target.value || null }); }} style={{ ...inp, width: 150, padding: '5px 8px', fontSize: 12.5 }} /></label>
          <button type="button" disabled={busy} onClick={() => apiFetch(`/certs/${c.id}/views`).then(setViews).catch(() => setViews([]))} style={{ ...btn(), padding: '6px 10px', fontSize: 12 }}>👁 Who looked</button>
          <button type="button" disabled={busy} onClick={remove} style={{ ...btn('#fff', '#b91c1c'), padding: '6px 10px', fontSize: 12, marginLeft: 'auto' }}>Remove</button>
        </div>
      )}
      {mode === 'own' && c.status !== 'approved' && <div><button type="button" disabled={busy} onClick={remove} style={{ ...btn('#fff', '#b91c1c'), padding: '5px 10px', fontSize: 12 }}>Remove</button></div>}
      {rejecting && (
        <div style={{ display: 'flex', gap: 6 }}>
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="What needs fixing? e.g. Blurry — retake in good light" style={{ ...inp, padding: '7px 9px', fontSize: 13 }} />
          <button type="button" disabled={busy || !note.trim()} onClick={() => patch({ status: 'rejected', review_note: note })} style={btn('#c2410c', '#fff', 'none')}>Send</button>
        </div>
      )}
      {views && <div style={{ fontSize: 12, color: '#374151', background: '#f8fafc', borderRadius: 8, padding: '6px 9px' }}>{views.length ? views.slice(0, 12).map((v, i) => <div key={i}>{v.viewer_name} · {new Date(v.viewed_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>) : 'Nobody has opened the pictures yet.'}</div>}
      {err && <div style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 700 }}>{err}</div>}
    </div>
  );
}

// The checklist of card types against what is on file — the "what am I missing" view.
export function CertChecklist({ types, current }) {
  const have = Object.fromEntries((current || []).map(c => [c.cert_type, c]));
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {(types || []).filter(t => t.key !== 'other').map(t => {
        const c = have[t.key]; const s = c ? (STATE[c.expiry_state] || STATE.none) : { bg: '#fff', fg: '#9ca3af', bd: '#e5e7eb' };
        return <span key={t.key} title={c ? STATE[c.expiry_state]?.text(c) : 'Not on file'} style={pill(s)}>{c ? (c.expiry_state === 'expired' ? '✕ ' : '✓ ') : '○ '}{t.label}</span>;
      })}
      {(current || []).filter(c => c.cert_type === 'other').map((c, i) => <span key={'o' + i} style={pill(STATE[c.expiry_state] || STATE.none)}>✓ {c.label}</span>)}
    </div>
  );
}

// Everything for one person: checklist, cards, add. Used by "My Cards" (userId null) and the
// desktop employee card (Office/Executive).
export function CertsPanel({ userId = null, compact = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [adding, setAdding] = useState(false);
  const load = React.useCallback(() => apiFetch(userId ? `/users/${userId}/certs` : '/me/certs').then(setData).catch(e => setErr(e?.error || 'Could not load the cards.')), [userId]);
  useEffect(() => { load(); }, [load]);
  if (err) return <div style={{ fontSize: 13, color: '#b91c1c' }}>{err}</div>;
  if (!data) return <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</div>;
  if (data.status_only) return <CertChecklist types={data.types} current={data.current} />;
  const mode = userId && data.can_manage ? 'manage' : 'own';
  const certs = data.certs || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <CertChecklist types={data.types} current={data.current} />
      {adding
        ? <div style={{ border: '1.5px solid #fed7aa', background: '#fffaf5', borderRadius: 12, padding: 12 }}><CertUploadForm types={data.types} forUserId={userId} onSaved={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} /></div>
        : <button type="button" onClick={() => setAdding(true)} style={{ ...btn('#e87722', '#fff', 'none'), alignSelf: compact ? 'flex-start' : 'stretch', padding: '10px 14px' }}>＋ {userId ? 'Add a card' : 'Upload a card'}</button>}
      {certs.length === 0 && !adding && <div style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: '8px 0' }}>{userId ? 'No cards on file yet.' : 'Nothing uploaded yet. Take a photo of your license, OSHA card, CPR card… and send it to the office.'}</div>}
      {certs.map(c => <CertRow key={c.id} c={c} mode={mode} onChanged={load} />)}
    </div>
  );
}

// The phone's "My Cards" sheet.
export function MyCardsSheet({ onClose }) {
  return createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#111' }}>🪪 My Cards</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>License, OSHA, CPR, DOT physical… the office checks each one.</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, color: '#999', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: 16, paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}>
          <CertsPanel />
        </div>
      </div>
    </div>, document.body);
}
